import { useCallback, useMemo, useRef } from 'react';
import { css } from '@emotion/css';

import useScreen from '@lyearn/core/hooks/useScreen';
import { cn } from '@lyearn/style';
import { Box } from '@lyearn/ui';
import FilterActions from '@/components/Filter/FilterActions';
import useDebounceState from '@/hooks/useDebounceState';
import { FilterOperatorEnum } from '@/types/schema';

import useFilterListActions from '../../hooks/useFilterActions';
import FilterHeader from '../Header';
import FilterSearch from '../Search';
import FilterVirtualList from '../VirtualList';
import { FilterBodyProps } from './types';

const FILTER_ITEM_HEIGHT = 40;
const FILTER_HEADER_HEIGHT_MOBILE = 56;
const MAX_FILTERBODY_HEIGHT = 400;

const filterBodyStyle = (height: number) => css`
  .filter-items {
    height: ${height >= MAX_FILTERBODY_HEIGHT ? MAX_FILTERBODY_HEIGHT : height}px;
  }
`;

export const OPERATORS_WITHOUT_OPTION_LIST: FilterOperatorEnum[] = [
  FilterOperatorEnum.IsEmpty,
  FilterOperatorEnum.IsNotEmpty,
];

const FilterBody = ({
  headerProps,
  onChange,
  initialSelectedOptions,
  initialSelectedOperator,
  listConfig,
  uiConfig,
  queryParams,
  onToggle,
  className = 'w-[28rem] xs:w-full',
}: FilterBodyProps) => {
  const { isMobile } = useScreen();
  const [searchText, setSearchText, debouncedSearchText] = useDebounceState('');
  const { itemRenderer, listVariant, useOptionsQuery } = listConfig;
  const isSingleSelect = listVariant.includes('single');
  const {
    isHeaderEnabled = true,
    isSearchEnabled = true,
    showActionButtons = !isSingleSelect,
    searchPlaceholder,
  } = uiConfig ?? {};

  const { error, fetchMore, fetching, groups, renderFooter } = useOptionsQuery({
    searchText: debouncedSearchText,
    ...queryParams,
  });
  const totalOptions = useMemo(
    () => groups.reduce((acc, group) => acc + group.options.length, 0),
    [groups],
  );
  const isShowingSearch = useRef(
    isSearchEnabled && (totalOptions >= 8 || listVariant.includes('infinite')),
  );

  const { operatorsActions, optionsActions } = useFilterListActions({
    initialSelectedOptions: initialSelectedOptions ?? [],
    keyExtractor: (val) => val.value,
    initialSelectedOperator,
    showActionButtons,
    // onChange is only called when showActionButtons is false, for example in single select
    onChange: (...args) => {
      onChange(...args);
      // for cases when it's multi select without action buttons, closing should happen by clicking outside
      if (isSingleSelect) {
        onToggle?.(false);
      }
    },
  });

  const onSave = useCallback(() => {
    onChange(optionsActions.selectedOptions, operatorsActions.selectedOperator);
    onToggle?.(false);
  }, [onChange, onToggle, operatorsActions.selectedOperator, optionsActions.selectedOptions]);

  const onClear = useCallback(() => {
    onChange([]);
    onToggle?.(false);
  }, [onChange, onToggle]);

  const selectedSectionsCount = useMemo(() => {
    if (!searchText && listVariant === 'multi-ungrouped-finite') {
      return 1;
    }

    if (
      !searchText &&
      initialSelectedOptions?.length &&
      ['multi-grouped-infinite', 'multi-ungrouped-infinite'].includes(listVariant)
    ) {
      return initialSelectedOptions.length + 2;
    }

    return 0;
  }, [initialSelectedOptions, listVariant, searchText]);

  const memoizedBodyStyle = useMemo(() => {
    if (!totalOptions) {
      // Show loading indicator when initial fetch is happening
      return filterBodyStyle(fetching ? MAX_FILTERBODY_HEIGHT : 0);
    }

    const groupLabelsCount = groups.map((group) => group.label).filter(Boolean).length;

    const totalItemsCount = totalOptions + groupLabelsCount + selectedSectionsCount;

    // +8 is for top margin and +4 is fallback
    const itemHeight = isMobile ? FILTER_HEADER_HEIGHT_MOBILE : FILTER_ITEM_HEIGHT;
    return filterBodyStyle(totalItemsCount * itemHeight + 8 + 4);
  }, [fetching, groups, isMobile, selectedSectionsCount, totalOptions]);

  const shouldShowOptionList = useMemo(() => {
    if (!operatorsActions.selectedOperator) {
      return true;
    }
    const operatorRequiresList = OPERATORS_WITHOUT_OPTION_LIST.includes(
      operatorsActions.selectedOperator.id,
    );
    return !operatorRequiresList;
  }, [operatorsActions.selectedOperator]);

  return (
    <Box className={cn(className, memoizedBodyStyle)}>
      {isHeaderEnabled ? (
        <FilterHeader {...headerProps} operatorsActions={operatorsActions} />
      ) : null}
      {shouldShowOptionList ? (
        <Box className="h-full space-y-8 py-8">
          {isShowingSearch.current ? (
            <Box className="px-8 xs:px-24 xs:pb-10">
              <FilterSearch
                placeholder={searchPlaceholder}
                value={searchText}
                onChange={setSearchText}
              />
            </Box>
          ) : null}
          <FilterVirtualList
            fetchMore={fetchMore}
            fetching={fetching}
            groups={groups}
            initialSelectedOptions={initialSelectedOptions ?? []}
            isSearching={!!searchText}
            itemRenderer={itemRenderer}
            listVariant={listVariant}
            optionsActions={optionsActions}
            searchText={searchText}
            setSearchText={setSearchText}
          />
        </Box>
      ) : null}
      {renderFooter?.()}
      {showActionButtons ? <FilterActions onClear={onClear} onSave={onSave} /> : null}
    </Box>
  );
};

export default FilterBody;
